import {
  Crosshair,
  History,
  BrainCircuit,
  BookUser,
  Building2,
  CalendarDays,
  ClipboardCheck,
  Cpu,
  Database,
  FileCheck2,
  FlaskConical,
  Globe2,
  Handshake,
  Inbox,
  KeyRound,
  Layers,
  LayoutDashboard,
  Lock,
  Map,
  Network,
  ScrollText,
  Server,
  ShieldCheck,
  Siren,
  UserCog,
  Users,
  UsersRound,
  Video,
  Vote,
  Wallet,
  Workflow,
  FolderSearch,
  Satellite,
  Sparkles,
  Radio,
  MessageSquare,
} from "lucide-react";
import type { ComponentType } from "react";

/**
 * PORTED from pollsphere-bundle/src/components/tessera/app-shell.tsx.
 * Item ids, labels, icons, groups, badges and tags are PollSphere's — the
 * vocabulary is deliberately unchanged ("Command Centre", "Geography &
 * Electoral Units", "Vote Tallying Centre", …).
 *
 * The ONE addition is `phase`: set on anything the Axis backend does not serve
 * yet. Those render dimmed and unclickable, so the intended product shape stays
 * visible without offering dead routes. `phase: undefined` = live now.
 */

export interface NavChild {
  id: string;
  label: string;
  badge?: string;
  /** Axis route, for children of a live parent. */
  to?: string;
  /**
   * Backend authority required for THIS child, when it differs from its
   * parent's. The programmes group is gated on `program:read`, but the twelve
   * analytics screens are `program:analytics` — without a per-child gate a
   * `program:read` user was shown eleven links, all of which rendered a
   * Forbidden page.
   */
  permission?: string;
  /** When set, renders as a non-clickable section header above subsequent items */
  section?: string;
  phase?: number;
}

export type NavGroup =
  | "operations"
  | "intelligence"
  | "engagement"
  | "comms"
  | "finance"
  | "admin";

export interface NavItem {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  group: NavGroup;
  badge?: string;
  children?: NavChild[];
  tag?: "BETA" | "RESTRICTED" | "NEW" | "AUDIT";
  /** Axis route. Only set on live items. */
  to?: string;
  /** Backend authority required — the literal the API's @PreAuthorize uses. */
  permission?: string;
  /** Unset = shipped. Set = not built yet. */
  phase?: number;
  /**
   * Children come from the API rather than from this file. PollSphere hardcoded
   * its four integrations in the sidebar; ours are registered at runtime, so the
   * list has to be fetched. `AppShell` resolves this marker — the nav module
   * stays a plain data file with no network in it.
   */
  dynamic?: "integrations";
}

export const NAV_ITEMS: NavItem[] = [
  // ---- OPERATIONS -------------------------------------------------------
  {
    /*
       The landing page, and first in the list because it is where `/` now goes.

       This was a phase-15 placeholder. It is the screen that reads every module at once
       and ranks their findings by what perishes first, which is the question somebody
       signing in actually arrives with — `/` used to redirect to Administration, which
       answers a question almost nobody has.

       Ungated: it degrades per reader. Each source it reads carries its own catch, so a
       reader without a module's permission sees that area marked "not readable" rather
       than being refused the whole page.
    */
    id: "dashboard",
    label: "Command Centre",
    icon: LayoutDashboard,
    group: "operations",
    to: "/",
  },
  {
    // Third in the list on purpose. The Command Centre says what needs you today; this says
    // whether you are winning. Everything else in this nav is an input to one of those two.
    id: "victory",
    label: "Path to Victory",
    icon: Crosshair,
    group: "operations",
    badge: "NEW",
    to: "/victory",
    permission: "victory:read",
  },
  {
    // Immediately under Path to Victory, because it is what the projection rests on. A
    // reader who asks "where does that support number come from" should find the answer
    // one line below the number.
    id: "history",
    label: "Electoral History",
    icon: History,
    group: "operations",
    badge: "NEW",
    to: "/history",
    permission: "historical:read",
  },
  {
    // Second, and deliberately ungated. `GET /mobilizers/me` is authenticated-only
    // by design, and a field worker holds `mobilizer:task` — not necessarily
    // `mobilizer:read`. Filing this under Mobilizer Network, which IS gated on
    // `mobilizer:read`, would hide the one screen the seeded MOBILIZER role
    // exists for from every person who holds it. It is a personal screen, not an
    // administrative one, and it sits where a personal screen belongs.
    id: "my-day",
    label: "My Day",
    icon: ClipboardCheck,
    group: "operations",
    to: "/mobilizers/me",
  },
  {
    // LIVE as of Phase 2. PollSphere's child items (National Dashboard, Counties,
    // Constituencies, Wards, Polling Stations) are all the same screen with a
    // different `type` filter, so they collapse into the one route rather than
    // becoming five links that load the same component.
    id: "geography",
    label: "Geography & Electoral Units",
    icon: Map,
    group: "operations",
    to: "/geography",
    permission: "geography:read",
    badge: "LIVE",
    children: [
      { id: "units-counties", label: "Counties", to: "/geography?type=COUNTY" },
      {
        id: "units-constituencies",
        label: "Constituencies",
        to: "/geography?type=CONSTITUENCY",
      },
      { id: "units-wards", label: "Wards", to: "/geography?type=WARD" },
      {
        id: "units-stations",
        label: "Polling Stations",
        to: "/geography?type=POLLING_STATION",
      },
      {
        id: "units-import",
        label: "Import Hierarchy",
        to: "/geography?view=import",
      },
      { id: "units-history", label: "Historical Patterns", phase: 15 },
      { id: "units-recommendations", label: "AI Recommendations", phase: 14 },
    ],
  },
  {
    // Platform reference data, not a module's own. `turnout` scopes every screen to one
    // of these, and `survey` and `politics` will reference the same rows — so it sits at
    // the top level rather than inside whichever feature happened to need it first.
    id: "elections",
    label: "Elections",
    icon: CalendarDays,
    group: "operations",
    to: "/elections",
    permission: "election:read",
    badge: "NEW",
  },
  {
    // LIVE as of Phase 3. The register is never browsable in full — every child
    // link carries a filter, because the API refuses an unfiltered query.
    id: "voters",
    label: "Voter Registry",
    icon: Users,
    group: "operations",
    to: "/voters",
    permission: "voter:read",
    badge: "LIVE",
    children: [
      {
        id: "voters-supporters",
        label: "Supporters",
        to: "/voters?supportBand=SUPPORTER",
      },
      {
        id: "voters-undecided",
        label: "Undecided",
        to: "/voters?supportBand=UNDECIDED",
      },
      {
        id: "voters-unassessed",
        label: "Never assessed",
        to: "/voters?supportBand=UNASSESSED",
      },
      {
        id: "voters-unverified",
        label: "Unverified",
        to: "/voters?verificationStatus=UNVERIFIED",
      },
      {
        id: "voters-intel",
        label: "With open alerts",
        to: "/voters?hasOpenIntel=1",
      },
      // The vocabulary the occupation-based targeting presets match on. Gated on
      // `voter:read` like its parent — everybody who can see the register can see
      // which tags exist; `voter:tag` is what gates changing them, and the page
      // hides its own controls accordingly.
      { id: "voters-tags", label: "Tags", to: "/voters/tags", badge: "NEW" },
      { id: "voters-import", label: "Import Register", phase: 3 },
      { id: "voters-segments", label: "Segments", phase: 3 },
    ],
  },
  {
    // PollSphere's "Strategic Relocation" was an AI suggestion board — that is
    // Phase 5. What ships now is the half underneath it: the governed workflow
    // that corrects where a voter is registered. Named for what it does.
    id: "transfers",
    label: "Voter Transfers",
    icon: Network,
    group: "operations",
    to: "/transfers",
    permission: "relocation:read",
    badge: "LIVE",
    children: [
      { id: "transfers-queue", label: "Requests", to: "/transfers" },
      {
        id: "transfers-breached",
        label: "SLA breached",
        to: "/transfers?slaBreached=true",
      },
      {
        id: "transfers-batches",
        label: "Batches",
        to: "/transfers?view=batches",
      },
      // Its own screen rather than a tab: the queue answers "what do I do next" and this
      // answers "how are we doing", and they are read by different people.
      {
        id: "transfers-analysis",
        label: "Analysis",
        to: "/transfers/analysis",
        badge: "NEW",
      },
    ],
  },
  {
    // LIVE as of Phase 4. PollSphere's "Data Capture Studio" is a survey screen and
    // surveys are Phase 7; what a mobilizer can actually capture today is field
    // activity, so the item is named for that and lives under its own scope.
    // "Compare Mobilizers" is a panel on Performance Analytics rather than a route:
    // it shares that screen's date range, and two screens that both needed the same
    // range would drift the first time one of them grew a filter.
    id: "mobilizers",
    label: "Mobilizer Network",
    icon: Handshake,
    group: "operations",
    to: "/mobilizers",
    permission: "mobilizer:read",
    badge: "LIVE",
    children: [
      { id: "_sec_roster", label: "", section: "Roster & Field Ops" },
      { id: "mobilizers-roster", label: "Mobilizer Roster", to: "/mobilizers" },
      {
        id: "mobilizers-hierarchy",
        label: "Hierarchy & Roster",
        to: "/mobilizers/hierarchy",
      },
      {
        id: "mobilizers-adopt",
        label: "Adopt-a-Station",
        to: "/mobilizers/adoptions",
        badge: "NEW",
      },
      {
        id: "mobilizers-field",
        label: "Field Activity Feed",
        to: "/activities",
      },
      {
        id: "mobilizers-tasks",
        label: "Tasks & Missions",
        to: "/mobilizers/missions",
        badge: "NEW",
      },
      { id: "_sec_perf", label: "", section: "Performance & Analytics" },
      {
        id: "mobilizers-leaderboard",
        label: "Leaderboard",
        to: "/mobilizers/leaderboard",
        badge: "NEW",
      },
      {
        id: "mobilizers-analytics",
        label: "Performance Analytics",
        to: "/mobilizers/analytics",
        badge: "NEW",
      },
      { id: "_sec_strategy", label: "", section: "Strategy & Coverage" },
      {
        id: "mobilizers-coverage",
        label: "Coverage",
        to: "/mobilizers/coverage",
        badge: "NEW",
      },
      { id: "mobilizers-suggestions", label: "AI Adoption Board", phase: 5 },
    ],
  },
  {
    // Phase 6 — Know Your Polling Station Mate. The product name is kept; the
    // backend package is `turnout`. Live from Phase 6 — badge: 'NEW' not 'LIVE'
    // because it is both new and live.
    id: "station-mates",
    label: "Know Your Polling Station Mate",
    icon: UsersRound,
    group: "operations",
    badge: "NEW",
    to: "/station-mates",
    permission: "turnout:read",
    children: [
      { id: "sm-floor", label: "The Floor", to: "/station-mates",
        permission: "turnout:analyse" },
      { id: "sm-overview", label: "Overview", to: "/station-mates/overview" },
      // { id: 'sm-stations',   label: 'Stations',      to: '/station-mates/stations' },
      { id: "sm-cells", label: "Cells", to: "/station-mates/cells" },
      // {
      //   id: "sm-builder",
      //   label: "Cell builder",
      //   to: "/station-mates/builder",
      //   permission: "turnout:manage-cells",
      // },
      { id: "sm-pledges", label: "Pledges", to: "/station-mates/pledges" },
      {
        id: "sm-board",
        label: "My cell",
        to: "/station-mates/board",
        permission: "turnout:field",
      },
      { id: "sm-liveops", label: "Polling day", to: "/station-mates/live" },
      {
        id: "sm-transport",
        label: "Transport",
        to: "/station-mates/transport",
      },
      {
        id: "sm-incentives",
        label: "Incentives",
        to: "/station-mates/incentives",
        permission: "turnout:incentive-read",
      },
      {
        id: "sm-integrity",
        label: "Integrity",
        to: "/station-mates/integrity",
        permission: "turnout:moderate",
      },
      {
        id: "sm-analytics",
        label: "Analytics",
        to: "/station-mates/analytics",
        permission: "turnout:analyse",
      },
    ],
  },
  {
    // Near the top of Operations on purpose: it is the "what happened" screen, and the
    // question it answers is asked before the ones the feature screens answer. It grants
    // nothing by itself — every source on it is gated by its own feature's permission.
    id: "calendar",
    label: "Calendar",
    icon: CalendarDays,
    group: "operations",
    to: "/calendar",
    permission: "calendar:read",
    badge: "NEW",
  },
  {
    // The address book. Its own entry rather than a tab under something else: the question
    // "who do we know at the county" is asked on its own, usually in a hurry, and a contact
    // list buried two levels inside another module does not get used.
    id: "contacts",
    label: "Contacts",
    icon: BookUser,
    group: "operations",
    to: "/contacts",
    permission: "contact:read",
    badge: "NEW",
  },
  {
    // Campaign finance. Deliberately NOT a child of Programs: programme money reaches a
    // beneficiary and is accounted for per person, and this is what the campaign spends on
    // itself. Filing one under the other is how a platform ends up with two incompatible
    // answers for the same shilling.
    id: "finance",
    label: "Campaign Spending",
    icon: Wallet,
    group: "operations",
    to: "/finance",
    permission: "finance:read",
    badge: "NEW",
    children: [
      { id: "finance-analysis", label: "Where the money went", to: "/finance",
        permission: "finance:analyse" },
      { id: "finance-ledger", label: "The ledger", to: "/finance/ledger" },
      { id: "finance-prices", label: "What things cost", to: "/finance/prices",
        permission: "finance:analyse" },
      { id: "finance-statement", label: "Expenditure statement", to: "/finance/statement",
        permission: "finance:analyse" },
      { id: "finance-events", label: "Rallies & meetings", to: "/finance/events" },
    ],
  },
  {
    // PollSphere's four children were the four programme CATEGORIES, as though a
    // category were a place to go. A category is a filter on the list, and it is
    // one there. What deserves its own route is a QUESTION, so the children are
    // the analytics screens — which is also what stops twelve of them from being
    // twelve tabs inside a drawer nobody scrolls.
    id: "programs",
    label: "Programs & Target Groups",
    icon: Building2,
    group: "operations",
    to: "/programs",
    permission: "program:read",
    badge: "LIVE",
    children: [
      { id: "programs-list", label: "Programmes", to: "/programs" },
      {
        id: "programs-analytics",
        label: "Portfolio Overview",
        to: "/programs/analytics",
        badge: "NEW",
        permission: "program:analytics",
      },
      {
        id: "programs-groups",
        label: "Target Groups",
        to: "/programs/analytics/target-groups",
        permission: "program:analytics",
      },
      {
        id: "programs-overlap",
        label: "Overlap",
        to: "/programs/analytics/overlap",
        permission: "program:analytics",
      },
      {
        id: "programs-value",
        label: "Cost vs Uplift",
        to: "/programs/analytics/cost-vs-uplift",
        permission: "program:analytics",
      },
      {
        id: "programs-ecosystem",
        label: "Ecosystem",
        to: "/programs/analytics/ecosystem",
        permission: "program:analytics",
      },
      {
        id: "programs-coverage",
        label: "Coverage & Gaps",
        to: "/programs/analytics/coverage",
        permission: "program:analytics",
      },
      {
        id: "programs-geography",
        label: "Geography",
        to: "/programs/analytics/geography",
        permission: "program:analytics",
      },
      {
        id: "programs-money",
        label: "Money",
        to: "/programs/analytics/money",
        permission: "program:analytics",
      },
      {
        id: "programs-impact",
        label: "Impact",
        to: "/programs/analytics/impact",
        permission: "program:analytics",
      },
      {
        id: "programs-equity",
        label: "Equity & Reach",
        to: "/programs/analytics/equity",
        permission: "program:analytics",
      },
      {
        id: "programs-quality",
        label: "Delivery Quality",
        to: "/programs/analytics/quality",
        permission: "program:analytics",
      },
      {
        id: "programs-alerts",
        label: "Alerts",
        to: "/programs/analytics/alerts",
        permission: "program:analytics",
      },
    ],
  },
  {
    // PollSphere's Live Coverage was four tabs inside one module. Here they are
    // four routes, for the same reason the programme analytics screens are:
    // "what is on air right now" and "what did we record in October" are read by
    // different people at different moments, and a tab strip makes the second one
    // reachable only by going through the first.
    //
    // `meeting:read` gates seeing that a meeting exists; `meeting:join` gates being
    // handed a seat, and `meeting:host` the door. The nav uses the first, as coverage
    // does below: somebody who can see the diary but not take a seat should still find it.
    id: "meetings",
    label: "Meetings",
    icon: Video,
    group: "operations",
    to: "/meetings",
    permission: "meeting:read",
    children: [
      { id: "meetings-diary", label: "All Meetings", to: "/meetings" },
    ],
  },
  {
    // `coverage:read` gates seeing that a feed exists; `coverage:watch` gates being
    // handed the credential to see the pictures. The nav uses the first — an
    // account that can see the diary but not open a camera should still find it.
    id: "coverage",
    label: "Live Coverage",
    icon: Video,
    group: "operations",
    // LIVE only. Both badges together squeeze the label to "Live Co…" in the
    // sidebar, and between "this feature works now" and "this feature is new" the
    // first is the one somebody scanning the nav needs.
    to: "/coverage",
    permission: "coverage:read",
    badge: "LIVE",
    children: [
      { id: "coverage-live", label: "Live Now", to: "/coverage" },
      {
        id: "coverage-events",
        label: "Coverage Diary",
        to: "/coverage/events",
      },
      { id: "coverage-map", label: "Coverage Map", to: "/coverage/map" },
      { id: "coverage-archive", label: "Archive", to: "/coverage/archive" },
      {
        id: "coverage-golive",
        label: "Go Live",
        to: "/coverage/go-live",
        permission: "coverage:broadcast",
      },
      {
        id: "coverage-mine",
        label: "My Coverage",
        to: "/coverage/assignments",
      },
      {
        id: "coverage-analytics",
        label: "Coverage Analytics",
        to: "/coverage/analytics",
      },
    ],
  },
  {
    // The four that ship lost their `phase` and gained a route. The other four
    // KEEP theirs and stay dimmed: no server route exists for a coalition tree,
    // a candidate move, a movement history or competitor matching, and a live
    // link to a screen with nothing behind it is a promise. `politics` is
    // deliberately minimal — see `features/politics/README.md` for what is out of
    // scope and why none of it is stubbed.
    id: "political-landscape",
    label: "Coalitions, Parties & Candidates",
    icon: Network,
    group: "operations",
    tag: "NEW",
    to: "/political-landscape",
    permission: "politics:read",
    children: [
      { id: "pl-overview", label: "Overview", to: "/political-landscape" },
      {
        id: "pl-parties",
        label: "Parties",
        to: "/political-landscape/parties",
      },
      {
        id: "pl-coalitions",
        label: "Coalitions",
        to: "/political-landscape/coalitions",
      },
      {
        id: "pl-candidates",
        label: "Candidates",
        to: "/political-landscape/candidates",
      },
      { id: "pl-tree", label: "Coalition Tree", phase: 6 },
      { id: "pl-move", label: "Move Candidates", phase: 6 },
      { id: "pl-history", label: "Movement History", phase: 6 },
      { id: "pl-match", label: "Competitor Matching", phase: 6 },
    ],
  },
  // {
  //   id: "nominations",
  //   label: "Nominations & Certificates",
  //   icon: Award,
  //   group: "operations",
  //   tag: "NEW",
  //   phase: 6,
  //   children: [
  //     { id: "nom-applications", label: "Nomination Applications", phase: 6 },
  //     { id: "nom-certificates", label: "Issued Certificates", phase: 6 },
  //     { id: "nom-verify", label: "Certificate Verification", phase: 6 },
  //   ],
  // },
  {
    // Phase 8. `phase:` removed — that is what dimmed it — and the hardcoded "3" badge
    // dropped: a count baked into the nav is a number nobody updates and everybody reads.
    id: "incidents",
    label: "Incident Desk",
    icon: Siren,
    group: "operations",
    badge: "NEW",
    to: "/incidents",
    permission: "incident:read",
    children: [
      { id: "inc-register", label: "Register", to: "/incidents" },
      {
        id: "inc-report",
        label: "Report",
        to: "/incidents/report",
        permission: "incident:report",
      },
      { id: "inc-map", label: "Map", to: "/incidents/map" },
      {
        id: "inc-analytics",
        label: "Analytics",
        to: "/incidents/analytics",
        permission: "incident:analyse",
      },
    ],
  },
  {
    // Phase 9. `phase:` removed — that is what dimmed it. Everything under here is scoped
    // to the election chosen in the sub-bar.
    id: "tallying",
    label: "Vote Tallying Centre",
    icon: FileCheck2,
    group: "operations",
    badge: "NEW",
    to: "/tallying",
    permission: "politics:candidacy-read",
    children: [
      // The ballot leads: there is nothing to tally until one exists.
      // EVERY child carries a `to`. A child with `phase` and no route renders dimmed and
      // dead — which is how a shipped, working feature spent a release looking unbuilt.
      // These were left at `phase: 9` when Phase 9 actually landed.
      { id: "tally-ballot", label: "Ballot & Nominations", to: "/tallying" },
      { id: "tally-import", label: "Import", to: "/tallying/import" },
      { id: "tally-board", label: "Live board", to: "/tallying/board" },
      { id: "tally-seats", label: "Seat dashboards", to: "/tallying/seats" },
      { id: "tally-forms", label: "Forms & OCR", to: "/tallying/forms" },
      { id: "tally-returns", label: "Returns", to: "/tallying/returns" },
      { id: "tally-variance", label: "Variance", to: "/tallying/variance" },
      {
        id: "tally-reconciliation",
        label: "Reconciliation",
        to: "/tallying/reconciliation",
      },
      { id: "tally-disputes", label: "Disputes", to: "/tallying/disputes" },
      {
        id: "tally-certificates",
        label: "Certificates",
        to: "/tallying/certificates",
      },
      { id: "tally-analytics", label: "Analytics", to: "/tallying/analytics" },
    ],
  },

  {
    id: "nominations",
    label: "Nominations & Party Tickets",
    icon: Vote,
    group: "operations",
    badge: "NEW",
    to: "/nominations",
    permission: "nomination:read",
    children: [
      // Members lead: nothing else in the module works without a roll.
      { id: "nom-members", label: "Members", to: "/nominations" },
      { id: "nom-contests", label: "Contests", to: "/nominations/contests" },
      { id: "nom-aspirants", label: "Aspirants", to: "/nominations/aspirants" },
      {
        id: "nom-results",
        label: "Results & tickets",
        to: "/nominations/results",
      },
      { id: "nom-analytics", label: "Analytics", to: "/nominations/analytics" },
    ],
  },

  // ---- INTELLIGENCE -----------------------------------------------------
  {
    // Axis Advisory leads the Intelligence group because it is the only screen in
    // it that arrives rather than waits: the morning brief is pinned to its first
    // tab. An advisory console nobody opens is worth nothing, and burying it below
    // two analytics modules is how that happens.
    id: "advisory",
    label: "Axis Advisory",
    icon: Sparkles,
    group: "intelligence",
    to: "/advisory",
    permission: "advisor:read",
    badge: "NEW",
    children: [
      { id: "advisory-brief", label: "Brief", to: "/advisory" },
      { id: "advisory-console", label: "Console", to: "/advisory/console" },
      { id: "advisory-analyst", label: "Analyst", to: "/advisory/analyst" },
      { id: "advisory-map", label: "Advisory map", to: "/advisory/map" },
      { id: "advisory-findings", label: "Findings", to: "/advisory/findings" },
      { id: "advisory-proposals", label: "Proposals", to: "/advisory/proposals" },
      { id: "advisory-studio", label: "Studio", to: "/advisory/studio" },
      { id: "advisory-runs", label: "Runs", to: "/advisory/runs" },
      { id: "advisory-playbooks", label: "Playbooks", to: "/advisory/playbooks" },
      { id: "advisory-models", label: "Models", to: "/advisory/models" },
      { id: "advisory-usage", label: "Usage", to: "/advisory/usage" },
    ],
  },
  {
    id: "dossier",
    label: "Dossier & Intelligence",
    icon: FolderSearch,
    group: "intelligence",
    to: "/dossier",
    permission: "intel:read",
    badge: "NEW",
    children: [
      { id: "dossier-assay", label: "The Assay", to: "/dossier" },
      { id: "dossier-subjects", label: "Subjects", to: "/dossier/subjects" },
      { id: "dossier-new", label: "New target", to: "/dossier/new", permission: "intel:create" },
      { id: "dossier-gaps", label: "Gaps", to: "/dossier/gaps" },
      { id: "dossier-plans", label: "Collection plans", to: "/dossier/plans", permission: "intel:collect" },
      { id: "dossier-runs", label: "Run monitor", to: "/dossier/runs" },
      { id: "dossier-sources", label: "Sources & grading", to: "/dossier/sources" },
      { id: "dossier-entry", label: "Manual entry", to: "/dossier/entry", permission: "intel:ingest" },
      { id: "dossier-import", label: "Bulk import", to: "/dossier/import", permission: "intel:ingest" },
      { id: "dossier-triage", label: "Triage queue", to: "/dossier/triage", permission: "intel:triage" },
      { id: "dossier-watches", label: "Watches", to: "/dossier/watches", permission: "intel:watch" },
      { id: "dossier-alerts", label: "Alerts", to: "/dossier/alerts" },
    ],
  },
  {
    // The seven child ids below are PollSphere's own, unchanged: the vocabulary is the
    // product's identity and renaming it would make the port a redesign.
    id: "opposition",
    label: "Opposition Command",
    icon: Satellite,
    group: "intelligence",
    to: "/opposition",
    permission: "opposition:read",
    badge: "NEW",
    children: [
      { id: "opp-matrix", label: "Threat Matrix", to: "/opposition" },
      { id: "opp-new", label: "Open a target", to: "/opposition/new", permission: "opposition:target" },
      { id: "opp-livefeed", label: "Live Survey Feed", to: "/opposition/live-feed" },
      { id: "opp-vulnerabilities", label: "Vulnerability Surfaces", to: "/opposition/surfaces" },
      { id: "opp-scandals", label: "Scandals & Legal", to: "/opposition/scandals" },
      { id: "opp-killchain", label: "Kill-Chain Operations", to: "/opposition/kill-chain" },
      { id: "opp-ops", label: "Active Operations", to: "/opposition/operations", permission: "opposition:operate" },
      { id: "opp-watchlist", label: "Watch List", to: "/opposition/watchlist" },
    ],
  },
  {
    // Its own menu, not a tab inside Opposition Command. The two share a vocabulary but not
    // a job: opposition work is research about a person, and this is SPEECH published in the
    // campaign's name. Burying the ledger under another module's tab strip is how the record
    // of what was said stops being the first thing anybody looks at.
    id: "warroom",
    label: "Social War Room",
    icon: Radio,
    group: "intelligence",
    to: "/war-room",
    permission: "warroom:read",
    badge: "NEW",
    children: [
      { id: "war-situation", label: "Situation", to: "/war-room" },
      { id: "war-threads", label: "Threads", to: "/war-room/threads" },
      { id: "war-subjects", label: "Subjects", to: "/war-room/subjects" },
      { id: "war-queue", label: "Approval queue", to: "/war-room/queue" },
      { id: "war-coordination", label: "Coordination", to: "/war-room/coordination" },
      { id: "war-ledger", label: "Ledger", to: "/war-room/ledger" },
      { id: "war-accounts", label: "Accounts & voices", to: "/war-room/accounts", permission: "warroom:accounts" },
      { id: "war-radar", label: "Radar", to: "/war-room/watches", permission: "warroom:watch" },
    ],
  },
  {
    // Its own menu, and NOT under the war room. The two both send messages and are otherwise
    // unrelated: the war room answers one hostile thread in public, and this talks to hundreds
    // of thousands of people who did not ask. Different consent, different cost, different
    // approval — and a shared menu would imply a shared control.
    id: "comms",
    label: "Communications",
    icon: MessageSquare,
    group: "operations",
    to: "/comms",
    permission: "comms:read",
    badge: "NEW",
    children: [
      { id: "comms-overview", label: "Overview", to: "/comms" },
      { id: "comms-campaigns", label: "Campaigns", to: "/comms/campaigns" },
      { id: "comms-compose", label: "Compose", to: "/comms/compose", permission: "comms:compose" },
      { id: "comms-templates", label: "Templates", to: "/comms/templates", permission: "comms:templates" },
      { id: "comms-suppress", label: "Do not contact", to: "/comms/suppressions", permission: "comms:suppress" },
      { id: "comms-replies", label: "Replies", to: "/comms/replies", permission: "comms:suppress" },
      { id: "comms-channels", label: "Channels", to: "/comms/channels", permission: "comms:channels" },
    ],
  },
  {
    id: "analytics",
    label: "Conversion Analytics",
    icon: BrainCircuit,
    group: "intelligence",
    phase: 15,
  },
  {
    // PollSphere renders SEVENTEEN tabs in one horizontal strip. The sidebar is
    // one level deep (`NavChild` has no `children`), so the four groups live in
    // `PollingLayout` inside the content area and the rail lists the screens
    // flat — the same shape the programme analytics screens use.
    //
    // `survey:read` gates the module. Individual screens carry their own
    // authority where it differs, and a screen the caller cannot open is not in
    // the strip.
    id: "polling",
    label: "Polling Studio",
    icon: Layers,
    group: "intelligence",
    to: "/polling",
    permission: "survey:read",
    badge: "NEW",
    children: [
      { id: "polling-estimator", label: "The Estimator", to: "/polling",
        permission: "survey:analyse" },
      { id: "polling-overview", label: "Overview", to: "/polling/overview" },
      { id: "polling-polls", label: "Polls", to: "/polling/polls" },
      { id: "polling-fieldwork", label: "Fieldwork", to: "/polling/fieldwork" },
      // The interviewer's half of Fieldwork, and the only screen in this module
      // somebody with just `survey:field` needs.
      { id: "polling-map", label: "Map", to: "/polling/map" },
      {
        id: "polling-my-work",
        label: "My work",
        to: "/polling/my-work",
        permission: "survey:field",
      },
      {
        id: "polling-interviews",
        label: "Interviews",
        to: "/polling/interviews",
        permission: "survey:read-responses",
      },
      {
        id: "polling-quality",
        label: "Quality",
        to: "/polling/quality",
        permission: "survey:moderate",
      },
      { id: "polling-configure", label: "Configure", to: "/polling/configure" },
      { id: "polling-builder", label: "Builder", to: "/polling/builder" },
      { id: "polling-sample", label: "Sample", to: "/polling/sample" },
      { id: "polling-trackers", label: "Trackers", to: "/polling/trackers" },
      {
        id: "polling-results",
        label: "Results",
        to: "/polling/results",
        permission: "survey:analyse",
      },
      {
        id: "polling-crosstabs",
        label: "Crosstabs",
        to: "/polling/crosstabs",
        permission: "survey:analyse",
      },
      {
        id: "polling-segments",
        label: "Segments",
        to: "/polling/segments",
        permission: "survey:analyse",
      },
      { id: "polling-verbatims", label: "Verbatims", to: "/polling/verbatims" },
      {
        id: "polling-exports",
        label: "Exports",
        to: "/polling/exports",
        permission: "survey:export",
      },
      {
        id: "polling-external",
        label: "External polls",
        to: "/polling/external",
      },
    ],
  },

  // ---- ADMINISTRATION ---------------------------------------------------
  // Everything below is LIVE — it maps to a controller that exists today.
  // Labels remain PollSphere's; only the routes are Axis's.
  {
    id: "roles",
    label: "Roles & Hierarchy",
    icon: ShieldCheck,
    group: "admin",
    to: "/roles",
    permission: "rbac:read",
  },
  {
    id: "user-management",
    label: "User Management",
    icon: UserCog,
    group: "admin",
    badge: "NEW",
    to: "/users",
    permission: "user:read",
  },
  {
    id: "sessions",
    label: "Active Sessions",
    icon: Server,
    group: "admin",
    to: "/users?view=sessions",
  },
  {
    id: "audit-log",
    label: "System Audit Log",
    icon: ScrollText,
    group: "admin",
    tag: "AUDIT",
    to: "/audit",
    permission: "audit:read",
  },
  {
    id: "notifications",
    label: "Notification Log",
    icon: Inbox,
    group: "admin",
    to: "/notifications",
    permission: "notification:read",
  },

  {
    // Was `phase: 15` with no route, so it rendered dimmed and dead. The feature exists
    // now: the register of every field holding personal data, a check that the schema
    // actually does what the register claims, and the log of people who asked about theirs.
    id: "data-vault",
    label: "Data Vault & PII",
    icon: Database,
    group: "admin",
    tag: "RESTRICTED",
    to: "/data-vault",
    permission: "vault:read",
    badge: "NEW",
    children: [
      { id: "vault-inventory", label: "PII inventory", to: "/data-vault" },
      { id: "vault-dsar", label: "Subject requests", to: "/data-vault/requests" },
    ],
  },
  {
    // Expands into one row per provider — see `dynamic` above and
    // features/integrations/navEntries.ts. The parent still routes to the
    // registry, which is the "all of them, with their health" view; the children
    // are how you reach a single one without going through a table.
    id: "integrations",
    label: "Integrations",
    icon: Workflow,
    group: "admin",
    to: "/integrations",
    permission: "integration:read",
    badge: "LIVE",
    dynamic: "integrations",
  },
  {
    id: "api-keys",
    label: "API Keys & Webhooks",
    icon: KeyRound,
    group: "admin",
    to: "/integrations?view=keys",
    permission: "apikey:read",
  },
  {
    id: "regions",
    label: "Multi-Country Config",
    icon: Globe2,
    group: "admin",
    phase: 15,
  },
  {
    id: "model-lab",
    label: "Model Lab",
    icon: FlaskConical,
    group: "admin",
    tag: "BETA",
    phase: 14,
  },
  {
    // Also `phase: 15` until now. Real probes, real schema state, real sizes.
    id: "infra",
    label: "Infrastructure",
    icon: Cpu,
    group: "admin",
    to: "/infrastructure",
    permission: "infrastructure:read",
    badge: "NEW",
  },
  {
    id: "administration",
    label: "Advanced Administration",
    icon: ShieldCheck,
    group: "admin",
    badge: "NEW",
    to: "/administration",
  },
];

export const GROUPS: {
  id: NavGroup;
  label: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  { id: "operations", label: "Operations", icon: Layers },
  { id: "intelligence", label: "Intelligence", icon: BrainCircuit },
  { id: "finance", label: "Finance & Compliance", icon: Wallet },
  { id: "comms", label: "Communications", icon: Inbox },
  { id: "admin", label: "Administration", icon: Lock },
];

export const TAG_STYLES: Record<NonNullable<NavItem["tag"]>, string> = {
  BETA: "bg-amber-50 text-amber-700 border-amber-200",
  RESTRICTED: "bg-red-50 text-red-700 border-red-200",
  NEW: "bg-emerald-50 text-emerald-700 border-emerald-200",
  AUDIT: "bg-sky-50 text-sky-700 border-sky-200",
};

export const isLive = (i: { phase?: number }) => i.phase === undefined;
